<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { FieldSchema } from '../../../types';
import type { GenericField } from '@Types';
import { getShelterNow } from '@Constants';
import { formatHoldLength, getHoldEndDate, getHoldTypeDays, getMovedHoldEndDate } from '../../../helpers';

export interface HoldStatus {
  days: number;
  id: string;
  name: string;
  position: number;
  value: string;
}

const props = defineProps<{
  error?: string;
  field?: FieldSchema;
  label?: string;
  options?: HoldStatus[];
}>();

const { t } = useI18n();

const model = defineModel<any>();

const commentModel = ref();
const customReasonModel = ref();
const endDateModel = ref<Date | null | undefined>(undefined);
const holdReasonModel = ref();
const holdTypeModel = ref<'holdStatus' | 'custom'>('holdStatus');
const previousStartDate = ref<Date | null>(null);
const startDateModel = ref<Date | null | undefined>(undefined);

const holdTypeOptions = computed((): GenericField[] => [
  {
    name: t('animals.individualPage.holdLimits.selectHoldStatus'),
    value: 'holdStatus',
  },
  {
    name: t('animals.individualPage.holdLimits.customStatus'),
    value: 'custom',
  },
]);
const lengthOfHold = computed(() => {
  if (!startDateModel?.value || !endDateModel?.value) return t('animals.individualPage.holdLimits.emptyLengthOfHold');

  return formatHoldLength(startDateModel.value, endDateModel.value);
});

const options = computed((): any[] | undefined => {
  const option = props?.field?.edit?.options || props?.options || [];

  if (!Array.isArray(option) || option.length === 0) return undefined;

  return option.slice().sort((a: any, b: any) => a.position - b.position);
});

const startDateModelComputed = computed({
  get: (): Date | undefined => startDateModel.value ?? undefined,
  set: (value: Date | undefined) => {
    startDateModel.value = value ?? undefined;
  },
});

const endDateModelComputed = computed({
  get: (): Date | undefined => endDateModel.value ?? undefined,
  set: (value: Date | undefined) => {
    endDateModel.value = value ?? undefined;
  },
});
watch(holdTypeModel, switchHoldType);
watch(holdReasonModel, setDatesFromHoldReason);
watch(startDateModel, onStartDateChange);
watch([endDateModel, commentModel, customReasonModel], setModel);
watch([holdReasonModel, customReasonModel], ([newHoldReason, newCustomReason]) => {
  if (!newHoldReason && !newCustomReason) clearAllModels();
});

function clearAllModels() {
  holdReasonModel.value = undefined;
  commentModel.value = undefined;
  customReasonModel.value = undefined;
  endDateModel.value = undefined;
  model.value = undefined;
  previousStartDate.value = null;
  startDateModel.value = undefined;
}

// ---------- New Hold Limits ----------

function switchHoldType() {
  holdReasonModel.value = undefined;
  customReasonModel.value = undefined;

  model.value = undefined;
}

function setDatesFromHoldReason() {
  if (!holdReasonModel.value) return;

  const holdDays = getHoldTypeDays(holdReasonModel.value);
  const now = getShelterNow();
  startDateModel.value = now;
  endDateModel.value = holdDays === null ? now : getHoldEndDate(now, holdDays);
  previousStartDate.value = now;

  setModel();
}

function setEndDateFromStart(newStart: Date) {
  const isCustomHold = holdTypeModel.value === 'custom';
  const holdDays = isCustomHold ? null : getHoldTypeDays(holdReasonModel.value);

  endDateModel.value = getMovedHoldEndDate(newStart, previousStartDate.value, endDateModel.value, holdDays);
}

function onStartDateChange() {
  const newStart = startDateModel.value;
  const parsedStart = newStart ? new Date(newStart) : null;

  const isSameStartDate = parsedStart?.getTime() === previousStartDate.value?.getTime();
  if (isSameStartDate) return;

  if (!parsedStart) {
    previousStartDate.value = null;
    return;
  }

  setEndDateFromStart(parsedStart);
  previousStartDate.value = parsedStart;
  setModel();
}

function setModel() {
  const notValidValues = !holdReasonModel.value && !customReasonModel.value;
  if (notValidValues) return;

  if (!startDateModel.value || !endDateModel.value) return;

  const isCustomHoldStatus = holdTypeModel.value === 'custom';
  const resolvedLabel = isCustomHoldStatus ? customReasonModel.value : holdReasonModel.value?.label;

  if (!resolvedLabel) return;

  model.value = {
    hold_settings_id: isCustomHoldStatus ? null : holdReasonModel.value?._id?.$oid,
    label: resolvedLabel,
    name: isCustomHoldStatus ? null : holdReasonModel.value.name,
    color: isCustomHoldStatus ? null : holdReasonModel.value.color,
    start_date: startDateModel.value,
    end_date: endDateModel.value,
    custom_hold: isCustomHoldStatus,
    comment: commentModel.value,
  };
}
</script>

<template>
  <div>
    <div class="relative flex flex-col gap-6 pb-3 pt-1">
      <AFieldSeparator
        :class="[error ? 'bg-red-600' : 'bg-gray-300', 'absolute top-[-22px] z-[-1] h-[1px] border-0']"
      />

      <AFieldRadio
        v-model="holdTypeModel"
        :error
        :options="holdTypeOptions"
      />

      <div class="flex flex-col gap-1">
        <AFieldLabel
          :error
          :label="t('animals.individualPage.holdLimits.holdReason')"
          :required="field?.settings.required"
        />

        <AFieldDropdown
          v-if="holdTypeModel === 'holdStatus'"
          v-model="holdReasonModel"
          optionLabel="label"
          :error
          :options
        />

        <AFieldInputText
          v-else
          v-model="customReasonModel"
          :error
        />
      </div>

      <div class="flex flex-col gap-1">
        <AFieldLabel
          :error
          :label="t('common.startDate')"
          :required="field?.settings.required"
        />

        <MFieldDatePicker
          v-model="startDateModelComputed"
          removeClearIcon
          showTime
          :disabled="!holdReasonModel && !customReasonModel"
          :error
          :noModelOnStart="!field?.settings?.required && !holdReasonModel"
        />
      </div>

      <div class="flex flex-col gap-1">
        <AFieldLabel
          :error
          :label="t('common.endDate')"
          :required="field?.settings.required"
        />

        <MFieldDatePicker
          v-model="endDateModelComputed"
          removeClearIcon
          showTime
          :disabled="!holdReasonModel && !customReasonModel"
          :error
          :minDate="startDateModelComputed"
          :noModelOnStart="!field?.settings?.required && !holdReasonModel"
        />
      </div>

      <div class="flex gap-1">
        <AFieldLabel :label="t('animals.individualPage.holdLimits.lengthOfHold') + ':'" />

        <p class="text-sm">{{ lengthOfHold }}</p>
      </div>

      <div class="flex flex-col gap-1">
        <AFieldLabel :label="t('animals.individualPage.holdLimits.comments')" />

        <AFieldTextArea v-model="commentModel" />
      </div>
    </div>

    <AFieldSeparator :class="[error ? 'bg-red-600' : 'bg-gray-300', 'h-[1px] border-0']" />
  </div>
</template>
